/**
 * IndexedDB storage for uploaded document corpora.
 *
 * WHY NOT localStorage: a dropped folder is far too big. A few hundred pages
 * of extracted text blows past the ~5MB quota localStorage gives the whole
 * origin. IndexedDB has no such ceiling in practice.
 *
 * WHY NOT Postgres, when the CMS content itself lives there: a corpus is
 * reference material someone drops in to work against, not content the team
 * publishes. It is read constantly during a session (every corpus_grep scans
 * every file) and never needs to outlive the browser it was uploaded from.
 * Local keeps that scanning fast and costs nothing.
 *
 * The consequence is deliberate but worth knowing: a corpus is per-browser.
 * Uploading on one machine does not make it available on another, or to a
 * teammate. See useCorpora, where the attachment pointer stays local for
 * exactly this reason.
 *
 * Only EXTRACTED TEXT is stored. Binary originals are dropped after parsing:
 * they are 10-50x larger, and nothing downstream can read them anyway.
 */

const DB_NAME = "smart-cms-corpus"
const DB_VERSION = 1
const STORE_CORPORA = "corpora"
const STORE_FILES = "files"

/** One uploaded folder (or set of files). */
export interface Corpus {
  id: string
  /** Display name — the dropped folder's name, or a name the user typed. */
  name: string
  /**
   * Team this corpus belongs to.
   *
   * Scoping is enforced by the CALLER, not here: listCorpora filters by team,
   * and useCorpora only ever attaches ids that came from it. getCorpus and
   * filesOf take a bare id and will happily return another team's rows if
   * handed one, so do not use them as a security boundary.
   */
  teamId: string
  fileCount: number
  /** Total extracted text size, in bytes. */
  totalBytes: number
  createdAt: number
  /** Files that could not be parsed, kept so the UI can be honest about gaps. */
  skipped: Array<{ path: string; reason: string }>
}

/** One extracted document inside a corpus. */
export interface CorpusFile {
  /** `${corpusId}:${path}` — the primary key. */
  key: string
  corpusId: string
  /** Path relative to the dropped folder root, e.g. "policies/intake.md". */
  path: string
  /** Extracted plain text or markdown. Never binary. */
  text: string
  bytes: number
  /** Source format, for display and for deciding how to show a slice. */
  kind: "markdown" | "text" | "csv" | "json" | "html" | "pdf" | "docx" | "xlsx"
}

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_CORPORA)) {
        db.createObjectStore(STORE_CORPORA, { keyPath: "id" })
      }
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        const files = db.createObjectStore(STORE_FILES, { keyPath: "key" })
        // Every read is "all files in this corpus", so that is the index.
        files.createIndex("corpusId", "corpusId", { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode)
    const req = fn(t.objectStore(store))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  }))
}

export async function putCorpus(corpus: Corpus): Promise<void> {
  await tx(STORE_CORPORA, "readwrite", s => s.put(corpus))
}

export async function listCorpora(teamId: string): Promise<Corpus[]> {
  const all = await tx<Corpus[]>(STORE_CORPORA, "readonly", s => s.getAll())
  return all.filter(c => c.teamId === teamId).sort((a, b) => b.createdAt - a.createdAt)
}

export async function getCorpus(id: string): Promise<Corpus | null> {
  return (await tx<Corpus | undefined>(STORE_CORPORA, "readonly", s => s.get(id))) ?? null
}

export async function putFiles(files: CorpusFile[]): Promise<void> {
  const db = await open()
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_FILES, "readwrite")
    const store = t.objectStore(STORE_FILES)
    // One transaction for the whole batch: a folder is hundreds of writes and
    // a transaction each would be far slower and non-atomic.
    for (const f of files) store.put(f)
    t.oncomplete = () => resolve()
    t.onerror = () => reject(t.error)
  })
}

export async function filesOf(corpusId: string): Promise<CorpusFile[]> {
  const db = await open()
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE_FILES, "readonly")
    const req = t.objectStore(STORE_FILES).index("corpusId").getAll(corpusId)
    req.onsuccess = () => resolve((req.result as CorpusFile[]).sort((a, b) => a.path.localeCompare(b.path)))
    req.onerror = () => reject(req.error)
  })
}


export async function deleteCorpus(id: string): Promise<void> {
  const files = await filesOf(id)
  const db = await open()
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction([STORE_CORPORA, STORE_FILES], "readwrite")
    t.objectStore(STORE_CORPORA).delete(id)
    const fs = t.objectStore(STORE_FILES)
    for (const f of files) fs.delete(f.key)
    t.oncomplete = () => resolve()
    t.onerror = () => reject(t.error)
  })
}
